import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, BookBookmark, LinkSimple, DownloadSimple, FilePdf, FileDoc, FilePpt } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import EmptyState from "@/components/EmptyState";

const EMPTY = { title: "", description: "", url: "", category: "" };

function fileIcon(filename) {
  const ext = (filename || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return <FilePdf size={22} weight="duotone" />;
  if (["ppt", "pptx"].includes(ext)) return <FilePpt size={22} weight="duotone" />;
  return <FileDoc size={22} weight="duotone" />;
}

function formatSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} Mo` : `${Math.round(bytes / 1024)} Ko`;
}

export default function Bibliotheque() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("file"); // "file" ou "link"
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const canManage = user.role === "admin" || user.role === "professeur";

  const load = () => api.get("/resources").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY); setFile(null); setMode("file"); };

  const add = async () => {
    if (!form.title.trim()) return toast.error("Merci d'indiquer un titre.");
    if (mode === "file" && !file) return toast.error("Merci de choisir un fichier PDF, Word ou PowerPoint.");
    if (mode === "link" && !form.url.trim()) return toast.error("Merci d'indiquer un lien.");
    setSaving(true);
    try {
      if (mode === "file") {
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("description", form.description);
        fd.append("category", form.category);
        fd.append("file", file);
        await api.post("/resources/upload", fd);
      } else {
        await api.post("/resources", { title: form.title, description: form.description, url: form.url, category: form.category, type: "link" });
      }
      toast.success("Ressource ajoutée");
      setOpen(false); reset(); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/resources/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };

  const download = async (res) => {
    try {
      const r = await api.get(`/resources/${res.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = res.file_name || res.title;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="library-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Campus virtuel</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Bibliothèque numérique</h1>
          <p className="text-sm text-muted-foreground">Documents (PDF, Word, PowerPoint) et liens de référence partagés par les enseignants.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
            <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-resource-btn"><Plus size={18} className="mr-2" /> Ajouter</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouvelle ressource</DialogTitle></DialogHeader>
              <Tabs value={mode} onValueChange={setMode}>
                <TabsList>
                  <TabsTrigger value="file" data-testid="mode-file">Document (PDF/Word/PPT)</TabsTrigger>
                  <TabsTrigger value="link" data-testid="mode-link">Lien externe</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="space-y-3">
                <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="resource-title" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                {mode === "file" ? (
                  <div>
                    <Label>Fichier (PDF, Word ou PowerPoint)</Label>
                    <Input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="resource-file" />
                    {file && <p className="mt-1 text-xs text-muted-foreground">{file.name} · {formatSize(file.size)}</p>}
                  </div>
                ) : (
                  <div><Label>Lien (URL)</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="resource-url" /></div>
                )}
                <div><Label>Catégorie</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Management, Finance..." /></div>
              </div>
              <DialogFooter><Button onClick={add} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-resource-btn">{saving ? "Envoi..." : "Ajouter"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={BookBookmark}
          title="La bibliothèque est vide"
          description={canManage ? "Ajoutez un premier document (PDF, Word, PowerPoint) ou un lien de référence pour vos étudiants." : "Aucun document n'a encore été partagé par vos enseignants."}
          actionLabel={canManage ? "Ajouter une ressource" : undefined}
          onAction={canManage ? () => setOpen(true) : undefined}
          testId="library-empty"
        />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((r) => (
            <div key={r.id} className="flex flex-col rounded-xl border border-border bg-card p-5" data-testid={`resource-${r.id}`}>
              <div className="mb-3 flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
                  {r.type === "file" ? fileIcon(r.file_name) : <BookBookmark size={22} weight="duotone" />}
                </div>
                {r.category && <Badge variant="secondary">{r.category}</Badge>}
              </div>
              <h3 className="font-display text-lg font-bold">{r.title}</h3>
              <p className="mt-1 flex-1 text-sm text-muted-foreground">{r.description}</p>
              {r.type === "file" && <p className="mt-1 text-xs text-muted-foreground">{r.file_name} · {formatSize(r.file_size)}</p>}
              <p className="mt-2 text-xs text-muted-foreground">Ajouté par {r.added_by_name}</p>
              <div className="mt-3 flex items-center gap-2">
                {r.type === "file" ? (
                  <Button variant="outline" className="flex-1" onClick={() => download(r)} data-testid={`download-${r.id}`}>
                    <DownloadSimple size={16} className="mr-2" /> Télécharger
                  </Button>
                ) : (
                  <a href={r.url} target="_blank" rel="noreferrer" className="flex-1">
                    <Button variant="outline" className="w-full"><LinkSimple size={16} className="mr-2" /> Ouvrir</Button>
                  </a>
                )}
                {canManage && <Button size="icon" variant="ghost" onClick={() => del(r.id)}><Trash size={16} className="text-accent" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
